/**
 * Uruguayan money arithmetic: IVA, and the platform's commission.
 *
 * THE ONE COPY. Everything here used to exist twice — once in the backend
 * (`apps/backend/src/payments/uy-vat.ts` and `quote-fee-split.ts`) and once in
 * the portal's quote composer, where the local helpers carried comments saying
 * they "mirror the backend's". A mirror is a promise enforced by nothing, and
 * it broke exactly the way promises do: when the commission's base changed
 * from the honorarios to the full price, the server changed and the
 * professional's live preview kept quoting the old figure.
 *
 * That preview has to be computed client-side — it updates as the professional
 * types, before anything has been submitted — so the duplication cannot be
 * removed by having one side ask the other. What CAN be removed is the second
 * implementation, which is what this file does.
 *
 * WHY THIS PACKAGE AND NOT shared-utils, which is the better name: this one is
 * BUILT (dual CJS/ESM via tsc) and is already imported at runtime by both the
 * backend and the portal — `URUGUAY_BANKS` beside it is canonical data, not a
 * type. shared-utils is consumed as raw TypeScript through a Vite alias, which
 * the Nest build (rootDir `./src`, emitting to `dist/`) has never had to
 * handle. Correct naming is not worth a build that only fails on deploy.
 *
 * Everything is in MINOR units (centésimos) and returns integers. No floats
 * cross a boundary, and every rounding happens exactly once, here, so the
 * charge, the preview, the invoice and the payout cannot each round their own
 * way and disagree by a centésimo.
 */
/**
 * Uruguay's basic IVA rate.
 *
 * One constant because the rate had been written out separately in the catalog
 * pricing service, the Fiscalo payload builder, a quote's IVA and the portal's
 * composer. A rate that disagrees with itself across the charge, the invoice
 * and the payout is the kind of divergence nobody notices until DGI does.
 */
export const UY_VAT_BASIC_RATE = 0.22;
/**
 * IVA to ADD to a net amount.
 *
 * Rounded once, here, so the charge, the preview and the professional's payout
 * cannot each round differently and drift by a centésimo.
 */
export function uyVatOnNetMinor(netMinor) {
    return Math.round(netMinor * UY_VAT_BASIC_RATE);
}
/**
 * IVA already INSIDE a gross amount — the decomposition, not an addition.
 *
 * `uyVatOnNetMinor` answers "what do I add"; this answers "what is already in
 * here". THEY ARE NOT INVERSES and must never be swapped: on a 1.500 gross the
 * IVA inside is 270,49, while the IVA on 1.500 net is 330.
 *
 * Spec 76e23408 D6 made this the only one of the two that touches a quote.
 * What a professional enters is the final figure for their part, so the IVA is
 * extracted for display and never added to what anyone is charged.
 */
export function uyVatInGrossMinor(grossMinor) {
    return Math.round((grossMinor * UY_VAT_BASIC_RATE) / (1 + UY_VAT_BASIC_RATE));
}
/**
 * The platform's commission BEFORE its own IVA — the part Gestionalo keeps.
 *
 * THE BASE IS THE FULL PRICE, timbres and gastos included (rule 0eae036f). The
 * previous rule excluded pass-through money so the platform would not skim it,
 * which read correctly and ignored the cost side: the payment processor charges
 * its percentage on the FULL amount transacted, so a fee computed on honorarios
 * alone had to cover processing on money it never touched. At Mercado Pago's
 * 4,99% that only works when honorarios exceed ~50% of the total, and ~75% once
 * instalments are offered. A high-timbre escritura sits below both lines, and
 * the platform paid to process it.
 *
 * THIS IS THE FUNCTION THAT DRIFTED. The portal kept multiplying the honorarios
 * subtotal after the server moved to the full price, so a professional was
 * shown a total 24,40 lower than what their client would actually be charged.
 */
export function uyPlatformFeeNetMinor(priceMinor, feePercent) {
    return Math.round((priceMinor * feePercent) / 100);
}
/**
 * The platform fee the consumer pays ON TOP of the price, IVA included.
 *
 * THE COMMISSION IS NET PLUS IVA, NOT IVA-INCLUSIVE. It used to be the latter:
 * the percentage was the whole charge and the 22% inside it was the platform's
 * to remit, which meant a nominal 10% earned Gestionalo 8,197% and the gap was
 * invisible on every screen that said "10%". The percentage is now what the
 * platform actually keeps, and IVA is added on top the way it is for any other
 * supply.
 *
 * Built from the net rather than by growing a gross: rounding a gross and
 * dividing it back down does not always return the net it came from, and the
 * breakdown on screen has to add up to the figure charged.
 */
export function uyPlatformFeeGrossMinor(priceMinor, feePercent) {
    const netMinor = uyPlatformFeeNetMinor(priceMinor, feePercent);
    return netMinor + uyVatOnNetMinor(netMinor);
}
